import { sanitizeHtml } from '../lib/sanitize';
import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useLangPrefix, withLangPrefix } from '../hooks/useLangPrefix';
import { Button } from '../components/ui/button';
import { MapPin, ArrowRight, ShieldCheck, Recycle, Award, ChevronLeft, Leaf } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Combined city × formation landing page (e.g. /fr/sellier/marseille/automobile).
// Content is rendered server-side from a template — we just consume the JSON
// and lay it out in the boutique editorial style.
export const CityFormationPage = () => {
  const { slug, formation } = useParams();
  const { lang } = useLangPrefix();
  const L = (p) => withLangPrefix(p, lang);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/seo/city-formation/${slug}/${formation}`);
        if (!mounted) return;
        setData(res.data);
        document.title = res.data.meta_title;
        const meta = document.querySelector('meta[name="description"]');
        if (meta) meta.setAttribute('content', res.data.meta_description);
      } catch {
        if (mounted) setNotFound(true);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [slug, formation]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF7F0] flex items-center justify-center">
        <div className="animate-pulse text-[#1F2A4A]/40">Chargement…</div>
      </div>
    );
  }

  if (notFound || !data) {
    return (
      <div className="min-h-screen bg-[#FAF7F0] flex flex-col items-center justify-center px-6">
        <p className="text-xs font-mono uppercase tracking-[0.35em] text-[#C9B896] mb-3">404</p>
        <h1 className="font-serif italic text-4xl md:text-5xl text-[#1F2A4A] mb-6">Page introuvable.</h1>
        <Link to={L('/')} className="text-sm text-[#1F2A4A] hover:text-[#C9B896] underline underline-offset-4">
          ← Retour à l'accueil
        </Link>
      </div>
    );
  }

  const { city, formation: fmt, course, body_html } = data;
  const courseLink = course ? L(`/formation/${course.id}`) : L('/formations-sur-place');

  // JSON-LD : Course schema + LocalBusiness with areaServed
  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Course',
        name: `${fmt.long_label} à ${city.name}`,
        description: data.meta_description,
        provider: {
          '@type': 'EducationalOrganization',
          name: 'Sellerie Académie',
          sameAs: 'https://sellerie-academie.fr',
        },
        areaServed: { '@type': 'City', name: city.name },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Sellerie Académie', item: 'https://sellerie-academie.fr/fr/' },
          { '@type': 'ListItem', position: 2, name: city.name, item: `https://sellerie-academie.fr/fr/sellier/${city.slug}` },
          { '@type': 'ListItem', position: 3, name: fmt.long_label },
        ],
      },
    ],
  };

  return (
    <div className="min-h-screen bg-[#FAF7F0]" data-testid="city-formation-page">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      {/* Hero */}
      <section className="bg-[#1F2A4A] text-white pt-32 pb-12 lg:pt-40 lg:pb-16">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <div className="flex items-center gap-2 text-xs text-[#C9B896]/85 mb-6">
            <Link to={L('/')} className="hover:text-white transition-colors inline-flex items-center gap-1">
              <ChevronLeft className="w-3.5 h-3.5" /> Accueil
            </Link>
            <span className="opacity-50">/</span>
            <Link to={L(`/sellier/${city.slug}`)} className="hover:text-white transition-colors">{city.name}</Link>
            <span className="opacity-50">/</span>
            <span className="text-white/85 capitalize">{fmt.label}</span>
          </div>

          <p className="text-xs font-mono uppercase tracking-[0.35em] text-[#C9B896] mb-4">
            <MapPin className="inline w-3 h-3 mr-1.5" />
            {city.name} · {fmt.label}
          </p>
          <h1 className="font-serif font-light text-4xl md:text-6xl leading-[1.05] tracking-tight max-w-3xl text-[#FAF7F0]">
            {fmt.long_label}{' '}
            <span className="italic text-[#C9B896]">à {city.name}.</span>
          </h1>
          <p className="mt-5 text-base md:text-lg text-white/80 max-w-2xl font-light leading-relaxed">
            {fmt.intent}{' '}
            <span className="text-[#C9B896]">À {city.distance_km}&nbsp;km de notre atelier d'Istres.</span>
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link to={courseLink}>
              <Button className="bg-[#C9B896] text-[#0F1623] hover:bg-[#D9C8A8] rounded-full px-6 h-11" data-testid="city-fmt-cta-course">
                {course ? `Voir « ${course.title.slice(0, 36)}${course.title.length > 36 ? '…' : ''} »` : 'Voir les formations'}
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            <Link to={L('/inscription')}>
              <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 rounded-full px-6 h-11 bg-transparent" data-testid="city-fmt-cta-signup">
                Candidater
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="max-w-3xl mx-auto px-6 lg:px-12 py-14 lg:py-20">
        <div
          className="prose prose-lg max-w-none text-[#1F2A4A] [&_h2]:font-serif [&_h2]:text-2xl [&_h2]:md:text-3xl [&_h2]:font-light [&_h2]:text-[#1F2A4A] [&_h2]:mt-10 [&_h2]:mb-4 [&_p]:leading-relaxed [&_p]:text-[#1F2A4A]/85 [&_strong]:text-[#1F2A4A] [&_ul]:list-disc [&_ul]:pl-6 [&_li]:my-1.5 [&_li]:text-[#1F2A4A]/85"
          data-testid="city-fmt-body"
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(body_html) }}
        />

        {/* Eco impact teaser */}
        <Link
          to={L('/sellerie-et-environnement')}
          className="mt-10 flex items-start gap-4 p-6 rounded-2xl bg-emerald-50 border border-emerald-200 hover:border-emerald-400 transition-colors group"
          data-testid="city-fmt-eco-teaser"
        >
          <div className="w-11 h-11 rounded-xl bg-emerald-600 text-white flex items-center justify-center shrink-0">
            <Leaf className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <p className="text-xs font-mono uppercase tracking-wider text-emerald-700 mb-1">SELLERIE & TRANSITION ÉCOLOGIQUE</p>
            <p className="text-sm font-medium text-emerald-900">Réparer plutôt que jeter — un métier d'avenir, ancré dans le réemploi.</p>
            <p className="text-xs text-emerald-800/80 mt-1">Lire l'impact environnemental complet du métier de sellier-garnisseur →</p>
          </div>
        </Link>

        {/* Trust strip */}
        <div className="mt-10 pt-10 border-t border-[#1F2A4A]/10 grid grid-cols-2 md:grid-cols-3 gap-5">
          {[
            { Icon: ShieldCheck, label: 'Qualiopi', sub: 'Certifié' },
            { Icon: Award, label: 'Formateur unique', sub: 'Compagnonnage' },
            { Icon: Recycle, label: 'Métier durable', sub: 'Réparation & réemploi' },
          ].map((it) => (
            <div key={it.label} className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#C9B896]/15 flex items-center justify-center shrink-0">
                <it.Icon className="w-4 h-4 text-[#C9B896]" />
              </div>
              <div>
                <p className="text-sm font-medium text-[#1F2A4A]">{it.label}</p>
                <p className="text-[11px] text-[#1F2A4A]/60">{it.sub}</p>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-12 p-7 rounded-2xl bg-[#1F2A4A] text-white text-center">
          <h2 className="font-serif italic text-2xl md:text-3xl text-[#C9B896] mb-2">
            Prêt à venir de {city.name} ?
          </h2>
          <p className="text-sm text-white/75 mb-5">
            Nos sessions partent vite — un dossier en ligne, une réponse sous 48 h.
          </p>
          <Link to={L('/inscription')}>
            <Button className="bg-[#C9B896] text-[#0F1623] hover:bg-[#D9C8A8] rounded-full px-7 h-11" data-testid="city-fmt-cta-bottom">
              Candidater à la {fmt.long_label.toLowerCase()}
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default CityFormationPage;
